import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setCurrentService } from '../../store/slices/servicesSlice';
import { resetCheckout } from '../../store/slices/orderSlice';
import NotificationSystem from '../notifications/NotificationSystem';
import { ServiceIcons } from '../icons/ServiceIcons';
import HolographicText from './HolographicText';
import useReducedMotion from '../../hooks/useReducedMotion';
import { FaServer } from 'react-icons/fa';

const Header = () => {
  const prefersReducedMotion = useReducedMotion();
  
  return (
    <header className="relative bg-gradient-to-r from-slate-50/95 via-blue-50/90 to-cyan-50/95 backdrop-blur-xl border-b border-gray-200/50 sticky top-0 z-50 overflow-hidden">
      {/* Background futuriste */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0,133,201,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,133,201,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '30px 30px'
          }}
        />
        {!prefersReducedMotion && (
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-300/30 to-transparent animate-pulse" />
        )}
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <HolographicText 
                variant="primary" 
                size="xl" 
                glow={false} 
                animate={false}
                className="font-bold tracking-wide"
              >
                MapataCloud
              </HolographicText>
            </div>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <a
              href="/admin/auth"
              className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm shadow-md hover:shadow-lg"
            >
              <FaServer />
              <span className="hidden sm:inline">Panneau Admin</span>
              <span className="sm:hidden">Admin</span>
            </a>
            <div className="hidden lg:block text-sm text-gray-600 font-medium">
              Services Cloud Professionnels
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

const ServiceNavigation = ({ currentService, onServiceSelect }) => {
  const dispatch = useDispatch();
  const { serviceStatus } = useSelector(state => state.services);
  const prefersReducedMotion = useReducedMotion();

  const handleServiceClick = (serviceId) => {
    // Clear forms when switching services via navigation
    dispatch(setCurrentService(serviceId));
    dispatch(resetCheckout());
    onServiceSelect(serviceId);
  };
  
  const services = [
    { id: 'cloudVPS', name: 'Cloud VPS', shortName: 'VPS', icon: ServiceIcons.CloudVPS },
    { id: 'orchestrator', name: 'Orchestrateur', shortName: 'Orch.', icon: ServiceIcons.Orchestrator },
    { id: 'dbaas', name: 'DBaaS', shortName: 'DB', icon: ServiceIcons.DBaaS },
    { id: 'domains', name: 'Domaines', shortName: 'Dom.', icon: ServiceIcons.Domains },
    { id: 'dedicatedServer', name: 'Serveur Dédié', shortName: 'Dédié', icon: ServiceIcons.DedicatedServer }
  ];

  return (
    <nav className="relative bg-white/95 backdrop-blur-xl border-b border-gray-200/50 sticky top-14 sm:top-16 z-40 shadow-sm">
      {/* Effet de scan horizontal */}
      
      <div className="relative max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        <div className="flex space-x-2 sm:space-x-4 lg:space-x-6 overflow-x-auto py-3 sm:py-4 scrollbar-hide">
          {services.map(({ id, name, shortName, icon: Icon }) => {
            const isEnabled = serviceStatus[id]?.enabled && !serviceStatus[id]?.maintenance;
            const isActive = currentService === id;
            
            return (
              <button
                key={id}
                onClick={() => isEnabled && handleServiceClick(id)}
                disabled={!isEnabled}
                className={`group relative flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap text-xs sm:text-sm flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary-200 border ${
                  isActive
                    ? 'bg-primary-500 text-white border-primary-500 shadow-md'
                    : isEnabled
                    ? 'text-gray-700 hover:bg-primary-50 hover:text-primary-600 hover:border-primary-300 border-gray-200 hover:shadow-sm'
                    : 'text-gray-400 cursor-not-allowed opacity-50 border-gray-200'
                } ${!prefersReducedMotion ? 'hover:scale-[1.02]' : ''}`}
                aria-label={`Sélectionner le service ${name}`}
                aria-pressed={isActive}
              >
                
                <Icon className="relative w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
                <span className="relative hidden sm:inline">{name}</span>
                <span className="relative sm:hidden">{shortName}</span>
                
                {!isEnabled && (
                  <span className="relative text-xs bg-orange-100 text-orange-700 px-1 sm:px-2 py-1 rounded hidden sm:inline">
                    {serviceStatus[id]?.maintenance ? 'Maintenance' : 'Indisponible'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

const Layout = ({ children, currentService, onServiceSelect }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <ServiceNavigation 
        currentService={currentService} 
        onServiceSelect={onServiceSelect} 
      />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {children}
      </main>
      <NotificationSystem />
    </div>
  );
};

export default Layout;
